import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, LogIn, LogOut as LogOutIcon, Plus, Trash2 } from 'lucide-react';
import {
  FOOD_INSPECTION_RESULT_LABELS,
  ROLES,
  STAFF_ATTENDANCE_STATUS_LABELS,
  ROLE_LABELS,
} from '@saesak/shared';
import type { FoodInspectionResult, StaffAttendanceStatus } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Spinner,
  Textarea,
} from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { formatTime, todayISO } from '@/lib/format';
import { useRole } from '@/store/auth';

type Tab = 'checklist' | 'food' | 'attendance' | 'worklife';

/**
 * 원 운영 도구 — P6 (체크리스트 · 식자재검수일지 · 근태관리)
 * 교직원 내부용. 소통이 주 목적인 앱이지만 원장의 일상 운영을 받친다.
 */
export function OpsPage() {
  const [tab, setTab] = useState<Tab>('checklist');
  const [date, setDate] = useState(todayISO());

  return (
    <Screen withNav={false}>
      <AppBar title="운영 도구" back />

      <div className="flex items-center gap-2 px-4 pt-3">
        <div className="scroll-x flex flex-1 gap-2 overflow-x-auto">
          <Chip tone={tab === 'checklist' ? 'primary' : 'neutral'} selected={tab === 'checklist'} onClick={() => setTab('checklist')}>
            체크리스트
          </Chip>
          <Chip tone={tab === 'food' ? 'primary' : 'neutral'} selected={tab === 'food'} onClick={() => setTab('food')}>
            식자재 검수
          </Chip>
          <Chip tone={tab === 'attendance' ? 'primary' : 'neutral'} selected={tab === 'attendance'} onClick={() => setTab('attendance')}>
            근태
          </Chip>
          <Chip tone={tab === 'worklife' ? 'primary' : 'neutral'} selected={tab === 'worklife'} onClick={() => setTab('worklife')}>
            워라밸
          </Chip>
        </div>
        {/* Input은 w-full이 기본이라 flex 줄에서는 래퍼로 폭을 잡아야 한다 */}
        <div className="w-40 shrink-0">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value || todayISO())} />
        </div>
      </div>

      {tab === 'checklist' && <ChecklistTab date={date} />}
      {tab === 'food' && <FoodTab date={date} />}
      {tab === 'attendance' && <AttendanceTab date={date} />}
      {tab === 'worklife' && <WorkLifeTab />}
    </Screen>
  );
}

// ── 워라밸 (§5.25) ──────────────────────────────────────────

interface WorkLifeSummary {
  month: string;
  workDays: number;
  totalMinutes: number;
  avgCheckInMinute: number | null;
  avgCheckOutMinute: number | null;
  leaveDays: number;
  halfDays: number;
  sickDays: number;
}

const minuteLabel = (minute: number | null) =>
  minute == null
    ? '—'
    : `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;

const hoursLabel = (minutes: number) =>
  minutes === 0 ? '0시간' : `${Math.floor(minutes / 60)}시간 ${minutes % 60}분`;

/** 내 한 달 근무를 한눈에 — 출퇴근 체크만으로 계산한다 */
function WorkLifeTab() {
  const [month, setMonth] = useState(todayISO().slice(0, 7));

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['ops', 'worklife', month],
    queryFn: () => apiData<WorkLifeSummary>(`/ops/attendance/worklife?month=${month}`),
    placeholderData: keepPreviousData,
  });

  const shiftMonth = (delta: number) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    setMonth(d.toISOString().slice(0, 7));
  };

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex items-center justify-between">
        <Button size="sm" variant="ghost" onClick={() => shiftMonth(-1)}>
          ← 지난달
        </Button>
        <p className="font-semibold">{month.replace('-', '년 ')}월</p>
        <Button size="sm" variant="ghost" onClick={() => shiftMonth(1)}>
          다음달 →
        </Button>
      </div>

      {isPending && <Spinner />}
      {isError && <ErrorNote message="불러오지 못했어요." onRetry={() => void refetch()} />}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Card>
              <p className="text-sm text-ink-sub">근무일</p>
              <p className="tnum mt-0.5 text-2xl font-bold">{data.workDays}일</p>
            </Card>
            <Card>
              <p className="text-sm text-ink-sub">누적 근무</p>
              <p className="tnum mt-0.5 text-2xl font-bold">{hoursLabel(data.totalMinutes)}</p>
            </Card>
            <Card>
              <p className="text-sm text-ink-sub">평균 출근</p>
              <p className="tnum mt-0.5 text-2xl font-bold">{minuteLabel(data.avgCheckInMinute)}</p>
            </Card>
            <Card>
              <p className="text-sm text-ink-sub">평균 퇴근</p>
              <p className="tnum mt-0.5 text-2xl font-bold">{minuteLabel(data.avgCheckOutMinute)}</p>
            </Card>
          </div>

          <Card>
            <p className="mb-1 text-sm font-semibold text-ink-sub">쉼표</p>
            <p className="text-base">
              연차 <b className="tnum">{data.leaveDays}</b>일 · 반차{' '}
              <b className="tnum">{data.halfDays}</b>회 · 병가{' '}
              <b className="tnum">{data.sickDays}</b>일
            </p>
            <p className="mt-1.5 text-sm text-ink-sub">
              출근·퇴근 체크만 하면 자동으로 계산돼요. 나를 돌보는 시간도 기록만큼 소중해요.
            </p>
          </Card>
        </>
      )}
    </div>
  );
}

// ── 체크리스트 ──────────────────────────────────────────────

interface Checklist {
  id: string;
  name: string;
  items: { id: string; label: string }[];
  run: { checkedItemIds: string[]; note: string | null } | null;
}

function ChecklistTab({ date }: { date: string }) {
  const isDirector = useRole() === ROLES.DIRECTOR;
  const [composing, setComposing] = useState(false);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['ops', 'checklists', date],
    queryFn: () => apiData<Checklist[]>(`/ops/checklists?date=${date}`),
    placeholderData: keepPreviousData,
  });

  const rows = data ?? [];

  return (
    <div className="flex flex-col gap-3 p-4">
      {isDirector && !composing && (
        <Button variant="outline" icon={<Plus size={16} />} onClick={() => setComposing(true)}>
          체크리스트 만들기
        </Button>
      )}
      {composing && <ChecklistComposer onClose={() => setComposing(false)} />}

      {isPending && <Spinner />}
      {isError && <ErrorNote message="불러오지 못했어요." onRetry={() => void refetch()} />}

      {!isPending && rows.length === 0 && !composing && (
        <EmptyState
          title="체크리스트가 없어요"
          body="안전점검·소독처럼 매일 반복하는 점검을 만들어 두면 하루 한 번 체크만 하면 돼요."
        />
      )}

      {rows.map((checklist) => (
        <ChecklistCard key={checklist.id} checklist={checklist} date={date} isDirector={isDirector} />
      ))}
    </div>
  );
}

function ChecklistCard({
  checklist,
  date,
  isDirector,
}: {
  checklist: Checklist;
  date: string;
  isDirector: boolean;
}) {
  const queryClient = useQueryClient();
  const [checked, setChecked] = useState<string[]>(checklist.run?.checkedItemIds ?? []);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['ops', 'checklists'] });

  const save = useMutation({
    mutationFn: () =>
      apiData(`/ops/checklists/${checklist.id}/run`, {
        method: 'POST',
        body: { date, checkedItemIds: checked },
      }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: () => apiData(`/ops/checklists/${checklist.id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  const done = checked.length === checklist.items.length;

  return (
    <Card>
      <div className="mb-2 flex items-center gap-2">
        <p className="font-semibold">{checklist.name}</p>
        <Chip tone={done ? 'primary' : 'neutral'}>
          {checked.length}/{checklist.items.length}
        </Chip>
        {isDirector && (
          <button
            type="button"
            aria-label="삭제"
            className="tap ml-auto grid h-9 w-9 place-items-center rounded-sm text-ink-sub hover:text-notice"
            onClick={() => {
              if (window.confirm(`${checklist.name} 체크리스트를 삭제할까요?`)) remove.mutate();
            }}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        {checklist.items.map((item) => {
          const on = checked.includes(item.id);
          return (
            <button
              key={item.id}
              type="button"
              onClick={() =>
                setChecked((prev) => (on ? prev.filter((id) => id !== item.id) : [...prev, item.id]))
              }
              className={`tap flex items-center gap-2 rounded-sm px-3 py-2 text-left text-base transition-colors ${
                on ? 'bg-primary-soft text-primary-strong' : 'bg-bg'
              }`}
            >
              <span
                className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border ${
                  on ? 'border-primary bg-primary text-white' : 'border-line-strong bg-surface'
                }`}
              >
                {on && <Check size={12} />}
              </span>
              {item.label}
            </button>
          );
        })}
      </div>

      <Button block size="sm" className="mt-2" loading={save.isPending} onClick={() => save.mutate()}>
        오늘 기록 저장
      </Button>
    </Card>
  );
}

function ChecklistComposer({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [itemsText, setItemsText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const items = itemsText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const create = useMutation({
    mutationFn: () => apiData('/ops/checklists', { method: 'POST', body: { name, items } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ops', 'checklists'] });
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '만들지 못했어요.'),
  });

  return (
    <Card>
      <p className="mb-2 font-semibold">체크리스트 만들기</p>
      <div className="flex flex-col gap-3">
        <Field label="이름" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="예: 아침 안전점검" />
        </Field>
        <Field label="항목 (한 줄에 하나)" required>
          <Textarea
            rows={4}
            value={itemsText}
            onChange={(e) => setItemsText(e.target.value)}
            placeholder={'출입문·현관 점검\n놀잇감 소독\n교실 환기'}
          />
        </Field>
        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}
        <div className="flex gap-2">
          <Button block loading={create.isPending} disabled={!name.trim() || items.length === 0} onClick={() => create.mutate()}>
            만들기
          </Button>
          <Button block variant="outline" onClick={onClose}>
            닫기
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ── 식자재검수일지 ──────────────────────────────────────────

interface FoodLog {
  id: string;
  itemName: string;
  supplier: string | null;
  quantity: string | null;
  temperature: number | null;
  result: FoodInspectionResult;
  note: string | null;
}

function FoodTab({ date }: { date: string }) {
  const queryClient = useQueryClient();
  const [itemName, setItemName] = useState('');
  const [supplier, setSupplier] = useState('');
  const [quantity, setQuantity] = useState('');
  const [temperature, setTemperature] = useState('');
  const [result, setResult] = useState<FoodInspectionResult>('PASS');
  const [error, setError] = useState<string | null>(null);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['ops', 'food-logs', date],
    queryFn: () => apiData<FoodLog[]>(`/ops/food-logs?date=${date}`),
    placeholderData: keepPreviousData,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['ops', 'food-logs'] });

  const add = useMutation({
    mutationFn: () =>
      apiData('/ops/food-logs', {
        method: 'POST',
        body: {
          date,
          itemName,
          supplier: supplier || undefined,
          quantity: quantity || undefined,
          temperature: temperature ? Number(temperature) : undefined,
          result,
        },
      }),
    onSuccess: () => {
      setItemName('');
      setSupplier('');
      setQuantity('');
      setTemperature('');
      setResult('PASS');
      refresh();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : '저장하지 못했어요.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiData(`/ops/food-logs/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  const rows = data ?? [];

  return (
    <div className="flex flex-col gap-3 p-4">
      <Card>
        <p className="mb-2 font-semibold">검수 기록 추가</p>
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Input placeholder="품목 (예: 우유)" value={itemName} onChange={(e) => setItemName(e.target.value)} />
            <Input placeholder="수량" className="w-28" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <Input placeholder="공급업체" value={supplier} onChange={(e) => setSupplier(e.target.value)} />
            <Input
              placeholder="온도 ℃"
              className="w-28"
              inputMode="decimal"
              value={temperature}
              onChange={(e) => setTemperature(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            {(Object.keys(FOOD_INSPECTION_RESULT_LABELS) as FoodInspectionResult[]).map((code) => (
              <Chip
                key={code}
                tone={result === code ? (code === 'PASS' ? 'primary' : 'secondary') : 'neutral'}
                selected={result === code}
                onClick={() => setResult(code)}
              >
                {FOOD_INSPECTION_RESULT_LABELS[code]}
              </Chip>
            ))}
            <Button
              size="sm"
              className="ml-auto"
              icon={<Plus size={14} />}
              disabled={!itemName.trim()}
              loading={add.isPending}
              onClick={() => add.mutate()}
            >
              추가
            </Button>
          </div>
          {error && (
            <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
              {error}
            </p>
          )}
        </div>
      </Card>

      {isPending && <Spinner />}
      {isError && <ErrorNote message="불러오지 못했어요." onRetry={() => void refetch()} />}

      {!isPending && rows.length === 0 && (
        <EmptyState title="이 날짜의 검수 기록이 없어요" body="식자재가 들어오면 품목별로 기록해 두세요." />
      )}

      {rows.map((row) => (
        <Card key={row.id} className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-semibold">
              {row.itemName}
              {row.quantity && <span className="ml-1.5 text-sm font-normal text-ink-sub">{row.quantity}</span>}
            </p>
            <p className="text-sm text-ink-sub">
              {[row.supplier, row.temperature != null ? `${row.temperature}℃` : null]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <Chip tone={row.result === 'PASS' ? 'primary' : 'secondary'}>
            {FOOD_INSPECTION_RESULT_LABELS[row.result]}
          </Chip>
          <button
            type="button"
            aria-label="삭제"
            className="tap grid h-9 w-9 place-items-center rounded-sm text-ink-sub hover:text-notice"
            onClick={() => remove.mutate(row.id)}
          >
            <Trash2 size={16} />
          </button>
        </Card>
      ))}
    </div>
  );
}

// ── 근태관리 ────────────────────────────────────────────────

interface MyAttendance {
  checkInAt: string | null;
  checkOutAt: string | null;
  status: StaffAttendanceStatus;
}

interface BoardRow {
  memberId: string;
  name: string;
  role: string;
  checkInAt: string | null;
  checkOutAt: string | null;
  status: StaffAttendanceStatus | null;
  note: string | null;
}

function AttendanceTab({ date }: { date: string }) {
  const queryClient = useQueryClient();
  const isDirector = useRole() === ROLES.DIRECTOR;
  const isToday = date === todayISO();

  const mine = useQuery({
    queryKey: ['ops', 'attendance', 'me', date],
    queryFn: () => apiData<MyAttendance | null>(`/ops/attendance/me?date=${date}`),
    placeholderData: keepPreviousData,
  });

  const board = useQuery({
    queryKey: ['ops', 'attendance', 'board', date],
    queryFn: () => apiData<BoardRow[]>(`/ops/attendance?date=${date}`),
    enabled: isDirector,
    placeholderData: keepPreviousData,
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['ops', 'attendance'] });

  const punch = useMutation({
    mutationFn: (action: 'check-in' | 'check-out') =>
      apiData(`/ops/attendance/${action}`, { method: 'POST' }),
    onSuccess: refresh,
  });

  const setStatus = useMutation({
    mutationFn: (input: { memberId: string; status: StaffAttendanceStatus }) =>
      apiData('/ops/attendance', { method: 'PATCH', body: { ...input, date } }),
    onSuccess: refresh,
  });

  return (
    <div className="flex flex-col gap-3 p-4">
      {/* 내 출퇴근 — 오늘만 찍을 수 있다 */}
      <Card>
        <p className="mb-1 font-semibold">내 근태</p>
        <p className="text-sm text-ink-sub">
          {mine.data?.checkInAt ? `출근 ${formatTime(mine.data.checkInAt)}` : '출근 전'}
          {mine.data?.checkOutAt ? ` · 퇴근 ${formatTime(mine.data.checkOutAt)}` : ''}
        </p>
        {isToday && (
          <div className="mt-2 flex gap-2">
            <Button
              block
              size="sm"
              icon={<LogIn size={14} />}
              disabled={Boolean(mine.data?.checkInAt)}
              loading={punch.isPending}
              onClick={() => punch.mutate('check-in')}
            >
              출근
            </Button>
            <Button
              block
              size="sm"
              variant="outline"
              icon={<LogOutIcon size={14} />}
              disabled={!mine.data?.checkInAt}
              loading={punch.isPending}
              onClick={() => punch.mutate('check-out')}
            >
              퇴근
            </Button>
          </div>
        )}
      </Card>

      {/* 원장 현황판 */}
      {isDirector && (
        <>
          {board.isPending && <Spinner />}
          {board.isError && (
            <ErrorNote message="현황을 불러오지 못했어요." onRetry={() => void board.refetch()} />
          )}
          {(board.data ?? []).map((row) => (
            <Card key={row.memberId}>
              <div className="flex items-center gap-2">
                <p className="font-semibold">{row.name}</p>
                <span className="text-sm text-ink-sub">
                  {ROLE_LABELS[row.role as keyof typeof ROLE_LABELS] ?? row.role}
                </span>
                <span className="ml-auto text-sm text-ink-sub">
                  {row.checkInAt ? formatTime(row.checkInAt) : '—'}
                  {' ~ '}
                  {row.checkOutAt ? formatTime(row.checkOutAt) : '—'}
                </span>
              </div>
              <div className="scroll-x mt-2 flex gap-1.5 overflow-x-auto">
                {(Object.keys(STAFF_ATTENDANCE_STATUS_LABELS) as StaffAttendanceStatus[]).map(
                  (code) => (
                    <Chip
                      key={code}
                      tone={(row.status ?? 'WORK') === code ? 'primary' : 'neutral'}
                      selected={(row.status ?? 'WORK') === code}
                      onClick={() => setStatus.mutate({ memberId: row.memberId, status: code })}
                    >
                      {STAFF_ATTENDANCE_STATUS_LABELS[code]}
                    </Chip>
                  ),
                )}
              </div>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}
